import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { PricingLadder } from "@/components/pricing/PricingLadder";
import { PlanBuilder } from "@/components/pricing/PlanBuilder";
import { WhatWeDo } from "@/components/WhatWeDo";
import { VIDEO, POSTER } from "@/lib/images";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const PROJECTS = [
  ["GHL system install & setup", "$1,000", "Your complete client infrastructure built: pipelines, calendars, agents configured, follow-up automation live."],
  ["Lead reactivation campaign", "$1,000 + 10%", "We put your existing database back to work and book appointments from leads you already paid for. You only share in what it actually produces — $1,000 plus 10% of collected revenue from booked reactivations."],
  ["Rebranding & positioning", "$1,000", "Logo suite, palette, brand voice guide, positioning strategy, and ten ready-to-run ads."],
  ["AI digital twin", "$300 + $99/mo", "Your cloned voice, video presence, and photo model — trained once, working everywhere. $300 setup, then $99/mo."],
  ["Website or landing page build", "$1,000", "Mobile-optimized, conversion-focused, connected to your CRM. Six months of web hosting included."],
  ["Custom automation", "from $500", "One bottleneck, mapped and automated. Tell us what's eating your week — we'll quote it.", { label: "Request a quote", href: "/contact" }],
];

const FAQ = [
  ["Who actually does the work — AI or people?", "Both, by design. AI agents execute; a named human expert directs them, reviews the output, and signs their name to it. That oversight is included at every tier and never costs extra."],
  ["Do I have to choose between marketing help and operations help?", "No — that's our job. The readiness check and your free audit identify where the constraint is, and the department points its capacity there. At Full Department, both sides run concurrently."],
  ["What does 'one priority' mean at Priority?", "Your proposal names it in writing — one constraint, like lead response or back-office automation, that the department owns end to end. When it's handled, we either point Priority at the next priority or you move up a tier."],
  ["What are boosts?", "Fixed monthly add-ons that raise your plan's capacity — more videos, more builds, another agent, another channel. Up to two per plan. If you need more than two, the next tier up almost always costs less for more, and the plan builder on this page will tell you when."],
  ["What does the Business DNA audit cost?", "Nothing. It's a call. We score your business across six dimensions and send you a written build plan whether you hire us or not."],
  ["Which AI agents do I get?", "Your audit prescribes the configuration. A practice missing calls gets a receptionist and scheduler; a firm with a review problem gets a reputation agent and sales agent. Same price, different roster."],
  ["What happens if I use all my voice minutes?", "We alert you before you get close. Included minutes are 100 on Priority, 250 on Full Team, and unlimited, subject to fair use on Full Department. You can add a minute pack or move up a tier — your operator will tell you which makes more sense. Nothing shuts off and nothing surprise-bills."],
  ["Do unused videos, builds, or minutes roll over?", "No. Your capacity resets monthly, like payroll."],
  ["Can I change tiers or cancel?", "Upgrade any time, effective immediately. Downgrade at your next cycle. Everything is month to month."],
  ["What does a project credit toward a subscription?", "20% of any one-time project applies to your first month with the department, if you hire within 30 days of delivery."],
  ["What happens to my systems if I leave?", "Your data and your accounts stay yours. We build in your workspace, not ours."],
];

const ProjectCard = ({ title, price, body, cta, testid }) => (
  <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-base)] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30" data-testid={testid}>
    <div className="flex items-baseline justify-between gap-3">
      <h4 className="text-base font-semibold tracking-tight text-[var(--ink)]">{title}</h4>
      <span className="shrink-0 text-base font-semibold text-[var(--ember)]">{price}</span>
    </div>
    <p className="mt-3 flex-1 text-[14px] leading-relaxed text-[var(--muted)]">{body}</p>
    {cta && (
      <a href={cta.href} className="mt-4 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-[var(--ember)] hover:underline" data-testid={`${testid}-cta`}>
        {cta.label} →
      </a>
    )}
  </div>
);

export default function Pricing() {
  return (
    <>
      {/* 1. Hero */}
      <section
        className="relative overflow-hidden bg-[var(--charcoal)] pt-36 pb-20 md:pt-44 md:pb-28"
        data-testid="pricing-hero"
      >
        {/* background video */}
        <video
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45"
          src={VIDEO.pricing}
          poster={POSTER.pricing}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--charcoal)] via-[var(--charcoal)]/75 to-[var(--charcoal)]/55"
          aria-hidden="true"
        />
        {/* ambient depth */}
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(120% 90% at 15% 0%, rgba(0,245,160,0.12) 0%, rgba(0,245,160,0) 45%), radial-gradient(90% 70% at 100% 100%, rgba(6,214,160,0.10) 0%, rgba(6,214,160,0) 50%)",
          }}
        />
        <div className="pointer-events-none absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-[var(--mint)]/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--mint)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--mint)]" />
              Pricing
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-7 t-hero text-white">
              Hire your AI department.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              One department, three capacity levels, and a named human expert is
              accountable for the result.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button to="/audit" variant="primary" data-testid="hero-readiness-cta">Install your department</Button>
              <Button href="#team-pricing" variant="secondaryDark" data-testid="hero-pricing-cta">See pricing</Button>
            </div>
            <p className="mt-4 text-sm text-white/55">
              Free Business DNA audit. Six scores. Not a pitch.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 1b. What we do — moved here, directly below the hero */}
      <WhatWeDo />

      {/* 2. Free readiness check — hidden per request */}

      {/* 3. Capacity levels */}
      <section id="team-pricing" className="scroll-mt-24 bg-[var(--bg-contrast)] py-24 md:py-32" data-testid="pricing-teams">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Capacity levels</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              One department. Pick your team.
            </h2>
          </Reveal>
          <div className="mt-12">
            <PricingLadder />
          </div>
        </div>
      </section>

      {/* 3b. Boosts + Build your plan */}
      <section id="boosts" className="scroll-mt-24 bg-[var(--bg-base)] py-24 md:py-32" data-testid="pricing-boosts">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Boosts</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              Tune your plan with boosts.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[var(--muted)] sm:text-lg">
              Every boost is a fixed monthly add-on. No credits, no math — you
              know exactly what you get and exactly what it costs.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="boost-cards">
            {[
              ["Video boost", "4 more short-form videos every month", "$149/mo"],
              ["Build boost", "2 more automation builds every month", "$299/mo"],
              ["Agent slot", "1 more AI agent from the roster", "$99/mo"],
              ["Channel boost", "1 more channel owned and managed", "$199/mo"],
            ].map(([t, d, p], i) => (
              <Reveal key={t} delay={i * 70}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-base font-semibold tracking-tight text-[var(--ink)]">{t}</h3>
                    <span className="shrink-0 text-base font-semibold text-[var(--ember)]">{p}</span>
                  </div>
                  <p className="mt-3 text-[14px] leading-relaxed text-[var(--muted)]">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <p className="mt-6 max-w-3xl text-[14px] leading-relaxed text-[var(--muted)]">
              Up to two boosts per plan. Need more than that? The next tier up
              almost always gets you more for less — the calculator below will
              tell you.
            </p>
          </Reveal>

          <div className="mt-12 max-w-3xl">
            <PlanBuilder />
          </div>
        </div>
      </section>

      {/* 5. One-time projects */}
      <section id="projects" className="scroll-mt-24 bg-[var(--bg-contrast)] py-24 md:py-32" data-testid="pricing-projects">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>One-time projects</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              Start with a project.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[var(--muted)] sm:text-lg">
              Not ready for a monthly plan? Every project is a fixed-price,
              one-time build — and 20% of it credits toward your first month if
              you hire the department within 30 days.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.map(([t, p, b, cta], i) => (
              <Reveal key={t} delay={(i % 3) * 70}>
                <ProjectCard title={t} price={p} body={b} cta={cta} testid={`project-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <section id="faq" className="scroll-mt-24 bg-[var(--bg-base)] py-24 md:py-32" data-testid="pricing-faq">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal><Label>FAQ</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              Clear answers before you hire.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map(([q, a], i) => (
                <AccordionItem key={q} value={`faq-${i}`} className="border-black/10" data-testid={`pricing-faq-${i}`}>
                  <AccordionTrigger className="py-5 text-left text-lg font-semibold tracking-[-0.01em] text-[var(--ink)] hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[var(--muted)]">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section id="final" className="scroll-mt-24 bg-[var(--dark)] text-[var(--dark-text)]" data-testid="pricing-final">
        <div className="mx-auto max-w-4xl px-6 py-28 text-center md:px-12 md:py-36">
          <Reveal>
            <h2 className="t-section">
              See what AI is costing you to ignore.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Your free Business DNA audit — six scores, real numbers, no pitch. In
              your inbox in days.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-10 flex justify-center">
              <Button to="/audit" variant="primary" data-testid="final-readiness-cta">Get my free audit</Button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
